import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultPageLayout } from "../components/layouts/DefaultPageLayout";
import { Button } from "../components/ui/button";
import { 
  FeatherArrowLeft,
  FeatherCheckCircle,
  FeatherClock,
  FeatherFlag,
  FeatherFile,
  FeatherEdit2,
  FeatherAlertTriangle,
  FeatherXCircle,
  FeatherCalendar,
  FeatherFileSmall,
  FeatherSearch,
  FeatherChevronLeft,
  FeatherChevronRight
} from "../components/ui/icons";
import { FaChevronDown, FaPlusSquare, FaMinusSquare } from 'react-icons/fa';
import { Avatar } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ProjectBadge } from "../components/ui/project-badge";
import { IconButton } from "../components/ui/IconButton";
import { IconWithBackground } from "../components/ui/IconWithBackground";
import { Tooltip } from "../components/ui/Tooltip";
import TaskTypeIcon from "../components/shared/TaskTypeIcon";
import TaskDueDate from "../components/shared/TaskDueDate";
import ClickableFeatureTag from "../components/shared/ClickableFeatureTag";
import BunnyLoader from "../components/ui/BunnyLoader";
import TaskNotifications from "../components/shared/TaskNotifications";
import { useTaskNotifications } from "../hooks/useTaskNotifications";
import { getBirdsviewData, getMemberTasks, getYesterdaySummary, getMemberWorkPlanHistory, getTaskConversationCountsBatch } from "../services/managerService";
import ManagerDiscussionMarker from "../components/shared/ManagerDiscussionMarker";
import { formatHours } from "../utils/date";
import TaskSummaryModal from "./components/TaskSummaryModal";
import { FeatherArrowRight } from "../components/ui/icons";
import api from "../services/api";
import { holidayApi } from "../services/holidayService";
import VersionCheckBanner from "../components/shared/VersionCheckBanner";
import HubBoardView from "./TheHub/HubBoardView";
import "./TheHub.css";

const HUB_VIEW_STORAGE_KEY = 'thehub-view-mode';

// Format task status for display (e.g. "in-progress" -> "In Progress")
const formatTaskStatusLabel = (status) => {
  if (!status) return 'Open';
  const s = String(status).toLowerCase();
  if (s === 'in-progress') return 'In Progress';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
};

const getStatusVariant = (status, hasUnclosedPastPlan) => {
  if (hasUnclosedPastPlan) return "error";
  
  switch (status.toLowerCase()) {
    case "at-risk":
    case "not-started":
      return "error";
    case "on approved leave":
      return "neutral";
    case "created":
    case "in progress":
    case "active":
      return "secondary";
    case "completed":
    case "submitted":
      return "success";
    default:
      return "default";
  }
};

const TheHub = () => {
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState([]);
  const [memberTasks, setMemberTasks] = useState({});
  const [tasksLoading, setTasksLoading] = useState({});
  const [tasksError, setTasksError] = useState({});
  const [mainDataLoading, setMainDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expandedTasks, setExpandedTasks] = useState({});
  const [expandedTaskPullRequests, setExpandedTaskPullRequests] = useState({});
  const [expandedGeneralPullRequests, setExpandedGeneralPullRequests] = useState({});
  const [yesterdaySummary, setYesterdaySummary] = useState([]);
  const [filterSectionCollapsed, setFilterSectionCollapsed] = useState(false);
  const [yesterdayLoading, setYesterdayLoading] = useState(true);
  const [yesterdayError, setYesterdayError] = useState(null);
  const [yesterdayCollapsed, setYesterdayCollapsed] = useState(true);
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
  const [taskHistoryData, setTaskHistoryData] = useState({}); // Cache for task history by member
  
  // New state for project filtering
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // New state for projects overview
  const [projectsOverview, setProjectsOverview] = useState([]);

  // New state to track when all member tasks are loaded
  const [allTasksLoaded, setAllTasksLoaded] = useState(false);

  // New state for date filtering
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // State for holidays
  const [holidays, setHolidays] = useState([]);

  // View mode: 'planned' | 'board' - persisted in localStorage
  const [hubViewMode, setHubViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(HUB_VIEW_STORAGE_KEY);
      return saved === 'board' || saved === 'planned' ? saved : 'planned';
    } catch {
      return 'planned';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(HUB_VIEW_STORAGE_KEY, hubViewMode);
    } catch {
      // ignore
    }
  }, [hubViewMode]);

  // Task notifications hook
  const { 
    notifications: taskNotifications, 
    loadNotifications: loadTaskNotifications, 
    getNotification, 
    isTaskLoading 
  } = useTaskNotifications();

  // Manager discussion counts keyed by task id
  const [conversationCounts, setConversationCounts] = useState({});

  // Helper function to check if selected date is today
  const isToday = (dateString) => {
    const today = new Date().toISOString().split('T')[0];
    return dateString === today;
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Helper function to get date string in YYYY-MM-DD format
  const getDateString = (date) => {
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchMainData = async () => {
      try {
        setMainDataLoading(true);
        setError(null);
        
        // Fetch data for selected date
        const birdsviewData = await getBirdsviewData(selectedDate);
        
        setTeamData(birdsviewData);
        
        setError(null);
        return birdsviewData; // Return the data for use in fetchMemberTasks
      } catch (err) {
        setError("Failed to load team data");
        console.error("Error fetching team data:", err);
        return []; // Return empty array in case of error
      } finally {
        setMainDataLoading(false);
      }
    };

    const fetchYesterdayData = async () => {
      try {
        setYesterdayLoading(true);
        setYesterdayError(null);
        
        const yesterdayData = await getYesterdaySummary();
        setYesterdaySummary(yesterdayData);
      } catch (err) {
        console.error("Error fetching yesterday's summary:", err);
        setYesterdayError("Failed to load yesterday's summary");
      } finally {
        setYesterdayLoading(false);
      }
    };

    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await api.get('/api/projects');
        const projectsData = response.data || [];
        setProjects(projectsData);
      } catch (err) {
        console.error("Error fetching projects:", err);
        // Don't set error state for projects as it's not critical
      } finally {
        setProjectsLoading(false);
      }
    };

    const fetchMemberTasks = async (teamData) => {
      // Get members with plans from the provided team data
      const membersWithPlans = teamData.filter(member => member.hasTodaysPlan && member.taskCount > 0);
      
      // Initialize loading states for members with plans
      const loadingStates = {};
      const errorStates = {};
      membersWithPlans.forEach(member => {
        loadingStates[member.id] = true;
        errorStates[member.id] = null;
      });
      setTasksLoading(loadingStates);
      setTasksError(errorStates);
      
      // Collect all tasks first
      const allMemberTasks = {};
      const allTaskIds = [];
      
      // Fetch tasks for each member individually
      for (const member of membersWithPlans) {
        try {
          const tasks = await getMemberTasks(member.id, selectedDate);
          
          allMemberTasks[member.id] = tasks;
          
          // Collect task IDs for notifications
          if (tasks && Array.isArray(tasks)) {
            tasks.forEach(task => {
              if (task.id) {
                allTaskIds.push(task.id);
              }
            });
          }
          
          setTasksError(prev => ({
            ...prev,
            [member.id]: null
          }));
        } catch (err) {
          console.error(`Error fetching tasks for member ${member.id}:`, err);
          setTasksError(prev => ({
            ...prev,
            [member.id]: "Failed to load tasks"
          }));
        } finally {
          setTasksLoading(prev => ({
            ...prev,
            [member.id]: false
          }));
        }
      }
      
      // Update memberTasks once with all collected data
      setMemberTasks(allMemberTasks);
      
      // Load notifications for all tasks at once
      if (allTaskIds.length > 0) {
        loadTaskNotifications(allTaskIds);
        // Load manager discussion counts for all tasks
        getTaskConversationCountsBatch(allTaskIds)
          .then(counts => setConversationCounts(counts || {}))
          .catch(err => console.error('Error loading manager discussion counts:', err));
      }
      
      setAllTasksLoaded(true);
    };

    // Only fetch yesterday data and projects once (not date-dependent)
    fetchYesterdayData();
    fetchProjects();

    // Fetch holidays for the selected date's year
    const fetchHolidays = async () => {
      try {
        const date = new Date(selectedDate);
        const year = date.getFullYear();
        const holidaysData = await holidayApi.getAll(year);
        // Ensure we have an array - handle different response formats
        const holidaysArray = Array.isArray(holidaysData) ? holidaysData : [];
        setHolidays(holidaysArray);
      } catch (err) {
        console.error("Error fetching holidays:", err);
        setHolidays([]);
      }
    };
    fetchHolidays();

    // Fetch main data and member tasks when selectedDate changes
    fetchMainData().then(fetchMemberTasks);
  }, [selectedDate]); // Add selectedDate as dependency

  // Helper function to format date as YYYY-MM-DD for comparison
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if date is a weekend (Saturday or Sunday)
  const isWeekend = (dateString) => {
    const date = new Date(dateString);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
  };

  // Check if date is a company holiday
  const isHoliday = (dateString) => {
    if (!holidays || !Array.isArray(holidays) || holidays.length === 0) return false;
    const date = new Date(dateString);
    const dateStringFormatted = formatDateString(date);
    const dateYear = date.getFullYear();
    
    return holidays.some(holiday => {
      if (!holiday.date) return false;
      if (holiday.year && holiday.year !== dateYear) return false;
      
      let holidayDateStr;
      if (typeof holiday.date === 'string') {
        holidayDateStr = holiday.date.split('T')[0].split(' ')[0];
      } else {
        // If it's a Date object, format it using UTC to avoid timezone shifts
        const holidayDate = new Date(holiday.date);
        const year = holidayDate.getUTCFullYear();
        const month = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(holidayDate.getUTCDate()).padStart(2, '0');
        holidayDateStr = `${year}-${month}-${day}`;
      }
      
      return holidayDateStr === dateStringFormatted;
    });
  };

  // Helper function to check if member card should be hidden
  const shouldHideMemberCard = (member) => {
    const isWeekendDay = isWeekend(selectedDate);
    const isHolidayDay = isHoliday(selectedDate);
    const hasWorkPlan = member.hasTodaysPlan && member.taskCount > 0;
    
    // Hide card if it's a weekend or holiday AND member has no work plan
    return (isWeekendDay || isHolidayDay) && !hasWorkPlan;
  };

  // Helper function to check if a member has tasks for the selected project
  const memberHasProjectTasks = (member) => {
    if (projectFilter === "all") return true;
    
    const memberTaskList = memberTasks[member.id] || [];
    const hasProjectTasks = memberTaskList.some(task => {
      const taskProjectId = task.project?.id;
      const matches = taskProjectId === projectFilter;
      return matches;
    });
    
    return hasProjectTasks;
  };

  // Helper function to handle project filter change
  const handleProjectFilterChange = (projectId) => {
    setProjectFilter(projectId);
    setDropdownOpen(false);
  };

  // Helper function to toggle dropdown
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Helper function to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.hub-project-filter-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Helper function to calculate projects overview
  const calculateProjectsOverview = () => {
    const projectMap = new Map();
    
    Object.values(memberTasks).forEach(tasks => {
      tasks.forEach(task => {
        if (task.project?.id && task.project?.name) {
          const projectId = task.project.id;
          const projectName = task.project.name;
          
          if (!projectMap.has(projectId)) {
            projectMap.set(projectId, {
              id: projectId,
              name: projectName,
              taskCount: 0
            });
          }
          
          projectMap.get(projectId).taskCount += 1;
        }
      });
    });
    
    return Array.from(projectMap.values())
      .sort((a, b) => b.taskCount - a.taskCount);
  };

  // Update projects overview when member tasks change
  useEffect(() => {
    if (Object.keys(memberTasks).length > 0) {
      const overview = calculateProjectsOverview();
      setProjectsOverview(overview);
    }
  }, [memberTasks]);

  // Flat list of all tasks and unique feature tags for ClickableFeatureTag (feature details modal)
  const { allTasksFromHub, allFeatureTagsFromHub } = useMemo(() => {
    const tasks = Object.values(memberTasks).flat().filter(Boolean);
    const tagMap = new Map();
    tasks.forEach(t => {
      if (t.feature_tag?.id) {
        tagMap.set(t.feature_tag.id, t.feature_tag);
      }
    });
    return {
      allTasksFromHub: tasks,
      allFeatureTagsFromHub: Array.from(tagMap.values())
    };
  }, [memberTasks]);

  const filteredTeam = teamData.filter(member => {
    // First filter by status
    const statusMatch = filter === "all" || member.status.toLowerCase() === filter.toLowerCase();
    
    // Then filter by project
    const projectMatch = memberHasProjectTasks(member);
    
    return statusMatch && projectMatch;
  });

  // Flattened tasks for Board view (same filtering as Planned)
  const { boardTasks, memberMap } = useMemo(() => {
    const map = {};
    const tasks = [];
    const visibleMembers = filteredTeam.filter(m => !shouldHideMemberCard(m));
    visibleMembers.forEach(member => {
      map[member.id] = { id: member.id, name: member.name };
      (memberTasks[member.id] || []).forEach(task => {
        tasks.push({ ...task, memberId: member.id });
      });
    });
    // Sort: due date asc, then priority (status), then title
    tasks.sort((a, b) => {
      const dueA = a.feature_tag?.due_date ? new Date(a.feature_tag.due_date).getTime() : Infinity;
      const dueB = b.feature_tag?.due_date ? new Date(b.feature_tag.due_date).getTime() : Infinity;
      if (dueA !== dueB) return dueA - dueB;
      const statusOrder = ['open', 'in-progress', 'completed', 'released'];
      const idxA = statusOrder.indexOf((a.status || '').toLowerCase());
      const idxB = statusOrder.indexOf((b.status || '').toLowerCase());
      if (idxA !== idxB) return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
      return (a.title || '').localeCompare(b.title || '');
    });
    return { boardTasks: tasks, memberMap: map };
  }, [filteredTeam, memberTasks, selectedDate, holidays]);

  const handleBoardStatusChange = (taskId, updatedTask, memberId) => {
    setMemberTasks(prev => ({
      ...prev,
      [memberId]: (prev[memberId] || []).map(t =>
        t.id === taskId ? { ...t, ...updatedTask } : t
      )
    }));
  };

  const toggleTaskExpansion = (memberId, taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [`${memberId}-${taskId}`]: !prev[`${memberId}-${taskId}`]
    }));
  };

  const toggleTaskPullRequestExpansion = (memberTaskId) => {
    setExpandedTaskPullRequests(prev => ({
      ...prev,
      [memberTaskId]: !prev[memberTaskId]
    }));
  };

  const toggleGeneralPullRequestExpansion = (memberId) => {
    setExpandedGeneralPullRequests(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  // Helper function to calculate task summary data
  const calculateTaskSummary = (task, memberId) => {
    // For TheHub, tasks come from a single work plan, so we might not have full history
    // We'll use the current task data and try to get history if available
    const history = taskHistoryData[memberId]?.[task.id] || [];
    
    if (history.length === 0) {
      return {
        totalHours: parseFloat(task.hours) || 0,
        occurrences: 1,
        work_plan_attachments: task.work_plan_attachments || []
      };
    }

    const totalHours = history.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);
    const occurrences = history.length;
    
    // Collect all attachments from history
    const attachments = [];
    history.forEach(entry => {
      if (entry.work_plan_attachments && entry.work_plan_attachments.length > 0) {
        attachments.push(...entry.work_plan_attachments);
      }
    });
    // Also include current task attachments
    if (task.work_plan_attachments && task.work_plan_attachments.length > 0) {
      attachments.push(...task.work_plan_attachments);
    }

    return {
      totalHours: totalHours.toFixed(1),
      occurrences,
      work_plan_attachments: attachments
    };
  };

  // Function to open task summary modal
  const openTaskSummaryModal = async (task, memberId) => {
    // Try to fetch task history if not already cached
    if (!taskHistoryData[memberId]?.[task.id]) {
      try {
        const historyData = await getMemberWorkPlanHistory(memberId);
        const taskHistory = [];
        
        // Extract history for this specific task
        if (historyData.workPlans) {
          historyData.workPlans.forEach(plan => {
            const planTask = plan.tasks?.find(t => t.id === task.id);
            if (planTask) {
              const dateObj = new Date(plan.created_at);
              taskHistory.push({
                date: dateObj,
                hours: planTask.hours || 0,
                notes: planTask.notes || '',
                status: plan.is_closed ? 'Completed' : 'In Progress',
                work_plan_attachments: planTask.work_plan_attachments || []
              });
            }
          });
        }
        
        // Cache the history
        setTaskHistoryData(prev => ({
          ...prev,
          [memberId]: {
            ...prev[memberId],
            [task.id]: taskHistory
          }
        }));
      } catch (err) {
        console.error('Error fetching task history:', err);
      }
    }
    
    const summary = calculateTaskSummary(task, memberId);
    setSelectedTaskForModal({
      ...task,
      ...summary
    });
  };

  // Function to close task summary modal
  const closeTaskSummaryModal = () => {
    setSelectedTaskForModal(null);
  };

  // Helper function to get task status
  const getTaskStatus = (taskId, memberId) => {
    // Try to get from cached history
    const history = taskHistoryData[memberId]?.[taskId];
    if (history && history.length > 0) {
      // Return the most recent status
      return history[history.length - 1].status === 'Completed' ? 'completed' : 'in-progress';
    }
    
    // Fallback to finding in current memberTasks
    for (const memberTaskList of Object.values(memberTasks)) {
      const task = memberTaskList.find(t => t.id === taskId);
      if (task) {
        // Try to infer from task data
        return 'in-progress'; // Default for TheHub tasks
      }
    }
    return 'open';
  };

  // Helper function to get status variant for badge (for modal)
  const getTaskStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'in-progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'released':
        return 'success';
      case 'open':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  // Helper function to get task history
  const getTaskHistory = (taskId, memberId) => {
    const history = taskHistoryData[memberId]?.[taskId] || [];
    return history;
  };

  const toggleFilterSection = () => {
    setFilterSectionCollapsed(!filterSectionCollapsed);
  };

  // Helper function to get loading statistics
  const getLoadingStats = () => {
    const totalLoading = Object.values(tasksLoading).filter(loading => loading).length;
    const totalWithTasks = teamData.filter(member => member.hasTodaysPlan && member.taskCount > 0).length;
    const loadedCount = totalWithTasks - totalLoading;
    
    return {
      totalLoading,
      totalWithTasks,
      loadedCount,
      isLoading: totalLoading > 0
    };
  };

  // Helper function to check if a member should show loading state
  const isMemberLoading = (member) => {
    return member.hasTodaysPlan && member.taskCount > 0 && tasksLoading[member.id];
  };

  // Show error if main data failed to load
  if (error) {
    return (
      <div className="hub-error-container">
        <div className="hub-error">{error}</div>
      </div>
    );
  }

  return (
    <DefaultPageLayout>
      <VersionCheckBanner />
      <div className="hub-dashboard-container">
        <div className="hub-header">
          <div className="hub-header-left">
            <Button
              variant="neutral-tertiary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/dashboard")}
            >
              Back
            </Button>
            <h1 className="hub-text-heading-2">Team Work Plans Overview</h1>
          </div>
          <div className="hub-header-actions">
            <div className="hub-view-toggle">
              <Button
                variant={hubViewMode === 'planned' ? 'brand-primary' : 'neutral-secondary'}
                size="small"
                onClick={() => setHubViewMode('planned')}
              >
                Planned
              </Button>
              <Button
                variant={hubViewMode === 'board' ? 'brand-primary' : 'neutral-secondary'}
                size="small"
                onClick={() => setHubViewMode('board')}
              >
                Board
              </Button>
            </div>
          </div>
        </div>

        {/* Floating Time Capsule Shortcut */}
        <div className="hub-floating-time-capsule">
          <Tooltip
            title="Time Capsule"
            description="View detailed retrospective of team work plans with interactive timeline"
            placement="left"
          >
            <Button
              variant="brand-primary"
              icon={<FeatherClock />}
              onClick={() => navigate("/time-capsule")}
              className="hub-time-capsule-fab"
            >
              <span className="hub-fab-text">Time Capsule</span>
              <div className="hub-fab-glow"></div>
            </Button>
          </Tooltip>
        </div>

        {/* Yesterday's Summary Section */}
        <div className="hub-yesterday-summary-section">
          <div className="hub-yesterday-summary-header">
            <div className="flex items-center gap-2">
              <IconWithBackground icon={<FeatherClock />} style={{ display: 'flex', alignItems: 'center' }} />
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>Yesterday's Work Summary</h2>
            </div>
            <div className="hub-yesterday-summary-controls">
              {!yesterdayLoading && !yesterdayError && (
                <div className="flex items-center gap-2">
                  <div className="hub-yesterday-summary-date">
                    {new Date(yesterdaySummary.actualDate || yesterdaySummary.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                  {yesterdaySummary.isAdjusted && (
                    <Badge variant="neutral" className="text-xs">
                      Adjusted
                    </Badge>
                  )}
                </div>
              )}
              <IconButton
                icon={<FaChevronDown />}
                className={`hub-collapse-toggle ${yesterdayCollapsed ? 'collapsed' : ''}`}
                onClick={() => setYesterdayCollapsed(!yesterdayCollapsed)}
              />
            </div>
          </div>
          
          {!yesterdayCollapsed && (
            <>
              {yesterdayLoading ? (
                <div className="flex items-center justify-center py-4">
                  <BunnyLoader 
                    size="medium" 
                    text="Loading yesterday's summary..."
                    animation="bounce"
                  />
                </div>
              ) : yesterdayError ? (
                <div className="flex items-center justify-center py-4">
                  <div className="flex items-center gap-2 rounded-md bg-error-50 px-3 py-2">
                    <IconWithBackground
                      variant="error"
                      icon={<FeatherAlertTriangle />}
                    />
                    <span className="text-body font-body text-error-900">
                      {yesterdayError}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="hub-team-stats-grid">
                    <div className="hub-stat-card">
                      <span className="stat-label">Total People</span>
                      <span className="stat-value">{yesterdaySummary?.teamStats?.totalPeople || 0}</span>
                    </div>
                    <div className="hub-stat-card">
                      <span className="stat-label">Above 7 Hours</span>
                      <span className="stat-value">{yesterdaySummary?.teamStats?.peopleAbove7Hours || 0}</span>
                    </div>
                    <div className="hub-stat-card">
                      <span className="stat-label">5-6.5 Hours</span>
                      <span className="stat-value">{yesterdaySummary?.teamStats?.peopleBetween5And6_5Hours || 0}</span>
                    </div>
                    <div className="hub-stat-card">
                      <span className="stat-label">Less Than 5 Hours</span>
                      <span className="stat-value">{yesterdaySummary?.teamStats?.peopleLessThan5Hours || 0}</span>
                    </div>
                    <div className="hub-stat-card">
                      <span className="stat-label">On Leave</span>
                      <span className="stat-value">{yesterdaySummary?.teamStats?.peopleOnLeave || 0}</span>
                    </div>
                    <div className="hub-stat-card">
                      <span className="stat-label">Total Team Hours</span>
                      <span className="stat-value">{(yesterdaySummary?.teamStats?.totalTeamHours || 0).toFixed(1)}</span>
                    </div>
                    <div className="hub-stat-card">
                      <span className="stat-label">Average Hours</span>
                      <span className="stat-value">{(yesterdaySummary?.teamStats?.averageTeamHours || 0).toFixed(1)}</span>
                    </div>
                  </div>

                  {yesterdaySummary?.members && yesterdaySummary.members.length > 0 ? (
                    <div className="hub-yesterday-summary-table">
                      <div className="hub-table-header">
                        <div className="hub-header-cell">Team Member</div>
                        <div className="hub-header-cell">Tasks</div>
                        <div className="hub-header-cell">Achieved</div>
                        <div className="hub-header-cell">% Achieved</div>
                        <div className="hub-header-cell">Hours</div>
                        <div className="hub-header-cell">Status</div>
                        <div className="hub-header-cell">Started</div>
                        <div className="hub-header-cell">Closed</div>
                        <div className="hub-header-cell">Performance</div>
                      </div>
                      <div className="hub-table-body">
                        {yesterdaySummary.members
                          .sort((a, b) => b.totalHours - a.totalHours)
                          .map(resource => {
                            const hasConcern = resource.totalTasks > 0 && resource.totalHours < 6.5;
                            const concernLevel = resource.totalTasks > 0 && resource.totalHours < 6 ? 'high' : 
                                               resource.totalTasks > 0 && resource.totalHours >= 6 && resource.totalHours <= 6.5 ? 'medium' : null;
                            
                            const getPerformanceStatus = () => {
                              if (!resource.hasPlan) return { label: 'On Leave', variant: 'neutral' };
                              if (!resource.isClosed) return { label: 'Workday Not Closed', variant: 'attention' };
                              if (resource.totalHours === 0) return { label: 'No Work', variant: 'neutral' };
                              if (resource.totalHours >= 7) return { label: 'Excellent', variant: 'success' };
                              if (resource.totalHours >= 6.5) return { label: 'Good', variant: 'success' };
                              if (resource.totalHours >= 5) return { label: 'Fair', variant: 'warning' };
                              return { label: 'Poor', variant: 'error' };
                            };
                            
                            const performance = getPerformanceStatus();
                            
                            return (
                              <div key={resource.id} className={`hub-table-row ${hasConcern ? `concern-${concernLevel}` : ''}`}>
                                <div 
                                  className="hub-table-cell hub-member-cell clickable"
                                  onClick={() => navigate(`/compact-work-plan-history/${resource.id}`)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="hub-member-info">
                                    <Avatar size="medium">
                                      {resource.name.charAt(0)}
                                    </Avatar>
                                    <span className="hub-member-name">{resource.name}</span>
                                  </div>
                                </div>
                                <div className="hub-table-cell">
                                  <span className="hub-cell-value">{resource.totalTasks}</span>
                                </div>
                                <div className="hub-table-cell">
                                  <div className="hub-hours-cell">
                                    <span className="hub-cell-value">{formatHours(resource.totalHours)}</span>
                                    {hasConcern && (
                                      <span 
                                        className={`hub-hours-indicator ${concernLevel}`}
                                        data-tooltip={concernLevel === 'high' ? 'Action Required: Hours below 6' : 'Medium Concern: Hours below 6.5'}
                                      >
                                        {concernLevel === 'high' ? '!' : 'i'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="hub-table-cell">
                                  <Badge variant={resource.isClosed ? "success" : "warning"}>
                                    {resource.isClosed ? "Completed" : "In Progress"}
                                  </Badge>
                                </div>
                                <div className="hub-table-cell">
                                  <span className="hub-cell-value">
                                    {resource.planStartedAt ? 
                                      new Date(resource.planStartedAt).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : '-'
                                    }
                                  </span>
                                </div>
                                <div className="hub-table-cell">
                                  <span className="hub-cell-value">
                                    {resource.planClosedAt ? 
                                      new Date(resource.planClosedAt).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : '-'
                                    }
                                  </span>
                                </div>
                                <div className="hub-table-cell">
                                  <Badge 
                                    variant={performance.variant}
                                    className={
                                      performance.label === 'On Leave' ? 'hub-no-work-plan-badge' : 
                                      performance.label === 'Workday Not Closed' ? 'hub-work-day-not-closed-badge' : ''
                                    }
                                  >
                                    {performance.label}
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <IconWithBackground
                          variant="neutral"
                          icon={<FeatherAlertTriangle />}
                        />
                        <p className="mt-2 text-body font-body text-subtext-color">
                          No team members found for yesterday's summary
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Filter Section */}
        <div className="hub-filter-section">
          {filterSectionCollapsed ? (
            /* Collapsed Filter View - Modern Icon with Key Info */
            <div className="hub-filter-collapsed" style={{ 
              backgroundColor: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: '12px', 
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              width: '30%',
              minWidth: '280px',
              // borderImage: 'linear-gradient(90deg, #4d0bff, #ffcc00) 1',
              borderImage: 'linear-gradient(90deg, rgb(255 246 99), rgb(236 13 95)) 1'
            }}
            onClick={toggleFilterSection}
            //onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'}
            //onMouseLeave={(e) => e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'}
            >
              <div className="flex items-center gap-3">
                <div style={{ 
                  
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FeatherSearch size={16} style={{ color: '#6b7280' }} />
                </div>
                <div className="flex flex-col">
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    {formatDateForDisplay(selectedDate)}
                  </span>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                    {filteredTeam.length} of {teamData.length} members
                    {projectFilter !== "all" && ` • ${projects.find(p => p.id === projectFilter)?.name}`}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div style={{ 
                  backgroundColor: filter === "all" ? '#f3f4f6' : 
                                filter === "active" ? '#dbeafe' : 
                                filter === "completed" ? '#d1fae5' : 
                                filter === "at-risk" ? '#fef3c7' : '#fecaca',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: filter === "all" ? '#6b7280' : 
                         filter === "active" ? '#1e40af' : 
                         filter === "completed" ? '#059669' : 
                         filter === "at-risk" ? '#d97706' : '#dc2626'
                }}>
                  {filter === "all" ? "ALL" : 
                   filter === "active" ? "ACTIVE" : 
                   filter === "completed" ? "COMPLETED" : 
                   filter === "at-risk" ? "AT RISK" : "NOT STARTED"}
                </div>
                <FeatherChevronRight size={16} style={{ color: '#9ca3af' }} />
              </div>
            </div>
          ) : (
            /* Expanded Filter View */
            <div className="hub-filter-container">
              <div className="hub-filter-header" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div className="flex items-center gap-2">
                  
                </div>
                <button 
                  onClick={toggleFilterSection}
                  style={{ 
                    backgroundColor: '#f3f4f6', 
                    border: 'none', 
                    borderRadius: '6px', 
                    padding: '6px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                >
                  <FeatherChevronLeft size={16} style={{ color: '#6b7280' }} />
                </button>
              </div>
              
              <div className="hub-filter-group">
                <label className="hub-filter-label">Date Filter</label>
                <div className="hub-date-filter-container">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="hub-date-input"
                  />
                </div>
              </div>
              
              <div className="hub-filter-group">
                <label className="hub-filter-label">Status Filter</label>
                <div className="hub-filter-buttons">
                  <Button
                    variant={filter === "all" ? "brand-primary" : "neutral-secondary"}
                    onClick={() => setFilter("all")}
                    size="small"
                  >
                    All
                  </Button>
                  <Button
                    variant={filter === "active" ? "brand-primary" : "neutral-secondary"}
                    onClick={() => setFilter("active")}
                    size="small"
                  >
                    Active
                  </Button>
                  <Button
                    variant={filter === "completed" ? "brand-primary" : "neutral-secondary"}
                    onClick={() => setFilter("completed")}
                    size="small"
                  >
                    Completed
                  </Button>
                  <Button
                    variant={filter === "at-risk" ? "brand-primary" : "neutral-secondary"}
                    onClick={() => setFilter("at-risk")}
                    size="small"
                  >
                    At Risk
                  </Button>
                  <Button
                    variant={filter === "not-started" ? "brand-primary" : "neutral-secondary"}
                    onClick={() => setFilter("not-started")}
                    size="small"
                  >
                    Not Started
                  </Button>
                </div>
              </div>
              
              <div className="hub-filter-group">
                <label className="hub-filter-label">Project Filter</label>
                <div className="hub-project-filter-container">
                  {projectsLoading ? (
                    <div className="hub-project-filter-loading">
                      <BunnyLoader 
                        size="small" 
                        text="Loading projects..." 
                        animation="bounce"
                      />
                    </div>
                  ) : (
                    <div className="hub-custom-dropdown">
                      <Button
                        variant={projectFilter === "all" ? "neutral-secondary" : "brand-secondary"}
                        icon={<FeatherFile />}
                        iconRight={<FaChevronDown />}
                        size="small"
                        onClick={toggleDropdown}
                        className={dropdownOpen ? 'hub-dropdown-open' : ''}
                      >
                        {projectFilter === "all" 
                          ? "All Projects" 
                          : projects.find(p => p.id === projectFilter)?.name || "Select Project"
                        }
                      </Button>
                      
                      {dropdownOpen && (
                        <div className="hub-dropdown-content">
                          <div 
                            className="hub-dropdown-item"
                            onClick={() => handleProjectFilterChange("all")}
                          >
                            <span>All Projects</span>
                            {projectFilter === "all" && <FeatherCheckCircle size={16} />}
                          </div>
                          {projects.map(project => (
                            <div 
                              key={project.id}
                              className="hub-dropdown-item"
                              onClick={() => handleProjectFilterChange(project.id)}
                            >
                              <span>{project.name}</span>
                              {projectFilter === project.id && <FeatherCheckCircle size={16} />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Filter Summary - Only show when expanded */}
          {!filterSectionCollapsed && (
            <div className="hub-filter-summary">
              <span className="hub-filter-summary-text">
                Showing {filteredTeam.length} of {teamData.length} team members
                {projectFilter !== "all" && (
                  <span className="hub-filter-summary-project">
                    {" "}working on {projects.find(p => p.id === projectFilter)?.name}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        
        {/* Today's Work Plans Section */}
        {!mainDataLoading && teamData.length > 0 && (
          <>
            <div className="hub-todays-plans-section">
              <div className="hub-todays-plans-header">
                <div className="flex items-center gap-2">
                  <IconWithBackground icon={<FeatherClock />} style={{ display: 'flex', alignItems: 'center' }} />
                  <h2 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>{isToday(selectedDate) ? "Today's Work Plans" : "Work Plans"}</h2>
                  {(isWeekend(selectedDate) || isHoliday(selectedDate)) && (
                    <div className="flex items-center gap-2 hub-day-type-indicator">
                      {isHoliday(selectedDate) && (
                        <div className="hub-holiday-badge">
                          <FeatherCalendar size={14} />
                          <span className="font-semibold">Holiday</span>
                        </div>
                      )}
                      {isWeekend(selectedDate) && (
                        <div className="hub-weekend-badge">
                          <FeatherCalendar size={14} />
                          <span className="font-semibold">Weekend Off</span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Show loading summary if any users are still loading */}
                  {getLoadingStats().isLoading && (
                    <div className="ml-4">
                      <BunnyLoader 
                        size="medium" 
                        text={`Loading tasks for ${getLoadingStats().totalLoading} team member${getLoadingStats().totalLoading !== 1 ? 's' : ''}...`}
                        animation="bounce"
                        containerClassName="items-center"
                      />
                      {getLoadingStats().totalWithTasks > 0 && (
                        <span className="text-xs text-subtext-color ml-8">
                          ({getLoadingStats().loadedCount}/{getLoadingStats().totalWithTasks} completed)
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="hub-todays-plans-date">
                  {formatDateForDisplay(selectedDate)}
                </div>
              </div>
            </div>
            
            {/* Projects Overview Section */}
            {projectsOverview.length > 0 && (
              <div className="hub-projects-overview-section">
                {/* <div className="hub-projects-overview-header">
                  <span className="hub-projects-overview-title">Project vs Task</span>
                </div> */}
                <div className="hub-projects-overview-badges">
                  {projectsOverview.map(project => (
                    <Tooltip key={project.id} content={project.name}>
                      <div className="hub-project-badge-item">
                        <ProjectBadge projectName={project.name} />
                        <span className="hub-project-task-count">{project.taskCount}</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}
            
            {hubViewMode === 'board' ? (
              <div className="hub-board-container">
                <HubBoardView
                  tasks={boardTasks}
                  memberMap={memberMap}
                  onTaskClick={openTaskSummaryModal}
                  onStatusChange={handleBoardStatusChange}
                  getNotification={getNotification}
                  isTaskLoading={isTaskLoading}
                  allFeatureTagsFromHub={allFeatureTagsFromHub}
                  allTasksFromHub={allTasksFromHub}
                  conversationCounts={conversationCounts}
                />
              </div>
            ) : (
            <div className="hub-task-grid">
              {filteredTeam.filter(member => !shouldHideMemberCard(member)).map(member => {
                const tasksComplete = (memberTasks[member.id] || []).filter(t =>
                  ['completed', 'released'].includes((t.status || '').toLowerCase())
                ).length;
                const percentAchieved = member.taskCount > 0 ? Math.round((tasksComplete / member.taskCount) * 100) : 0;
                const isNotStarted = member.status?.toLowerCase() === 'not-started';
                return (
                <div 
                  key={member.id}
                  className={`hub-task-card ${member.status === "at-risk" || member.status === "not-started" ? "error" : ""}`}
                >
                  <div className="flex w-full items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar size="large">
                        {member.name.charAt(0)}
                      </Avatar>
                      <div className="flex flex-col items-start gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-heading-3 font-heading-3 text-default-font">
                            {member.name}
                          </span>
                          {isMemberLoading(member) && (
                            <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-md border border-blue-200">
                              <BunnyLoader 
                                size="small" 
                                text="Loading..." 
                                animation="bounce"
                                textClassName="text-blue-700 font-medium"
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getStatusVariant(member.status, member.hasUnclosedPastPlan)}>
                            {member.status}
                          </Badge>
                          {member.hasUnclosedPastPlan && (
                            <Badge variant="warning" className="flex items-center gap-1">
                              <FeatherAlertTriangle size={14} />
                              Previous day not closed
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <IconButton
                      variant="neutral-tertiary"
                      icon={<FeatherClock />}
                      onClick={() => navigate(`/compact-work-plan-history/${member.id}`)}
                      tooltip="View Work Plan History"
                    />
                  </div>

                  <div className="flex w-full items-center justify-between rounded-md bg-neutral-50 px-6 py-4">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        Plan Status
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {member.hasTodaysPlan ? "Created" : "Not Created"}
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        Tasks
                      </span>
                      <div className="flex items-center gap-2">
                        <IconWithBackground
                          icon={member.taskCount > 0 ? <FeatherCheckCircle /> : <FeatherXCircle />}
                        />
                        <span className="text-body-bold font-body-bold text-default-font">
                          {member.taskCount}
                        </span>
                      </div>
                    </div>
                    {!isNotStarted && (
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        Achieved
                      </span>
                      <div className="flex items-center gap-2">
                        <IconWithBackground
                          variant={tasksComplete > 0 ? "success" : "neutral"}
                          icon={<FeatherCheckCircle />}
                        />
                        <span className={`text-body-bold font-body-bold hub-achieved-count hub-achieved-count-${percentAchieved === 100 ? 'full' : percentAchieved > 0 ? 'partial' : 'low'}`}>
                          {tasksComplete}
                        </span>
                      </div>
                    </div>
                    )}
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        Hours Logged
                      </span>
                      <div className="flex items-center gap-2">
                        <IconWithBackground
                          variant={member.hoursLogged === 0 ? "warning" : "default"}
                          icon={<FeatherClock />}
                        />
                        <span className="text-body-bold font-body-bold text-default-font">
                          {formatHours(member.hoursLogged)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {!isNotStarted && (
                  <div className={`hub-task-card-percent hub-task-card-percent-${percentAchieved === 100 ? 'full' : percentAchieved > 0 ? 'partial' : 'low'}`}>
                    <div className="hub-percent-row">
                      <span className="text-caption font-caption text-subtext-color">% Achieved</span>
                      <span className="hub-percent-value text-body-bold font-body-bold">
                        {percentAchieved}%
                      </span>
                    </div>
                    <div className="hub-percent-track">
                      <div
                        className={`hub-percent-fill ${member.taskCount === 0 ? '' : tasksComplete === member.taskCount ? 'hub-percent-fill-full' : 'hub-percent-fill-partial'}`}
                        style={{ width: `${member.taskCount > 0 ? (tasksComplete / member.taskCount) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  )}

                  <div className="flex w-full flex-col items-start gap-4">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Planned Tasks
                      </span>
                      <Badge
                        variant={getStatusVariant(member.status, member.hasUnclosedPastPlan)}
                        icon={<FeatherFlag />}
                      >
                        {member.taskCount}
                      </Badge>
                    </div>
                    <div className="flex w-full flex-col items-start gap-2">
                      {member.taskCount > 0 ? (
                        isMemberLoading(member) ? (
                          <div className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-6">
                            <BunnyLoader 
                              size="large" 
                              text={`Loading ${member.name}'s tasks...`}
                              animation="bounce"
                              textClassName="text-blue-700 font-medium"
                            />
                          </div>
                        ) : tasksError[member.id] ? (
                          <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-3 py-2">
                            <IconWithBackground
                              variant="error"
                              icon={<FeatherAlertTriangle />}
                            />
                            <span className="text-body font-body text-error-900">
                              {tasksError[member.id]}
                            </span>
                          </div>
                        ) : (
                          memberTasks[member.id]?.map(task => (
                            <div 
                              key={task.id} 
                              className="flex w-full flex-col rounded-md bg-neutral-50 cursor-pointer hover:bg-neutral-100 hub-task-card-expandable"
                              onClick={() => toggleTaskExpansion(member.id, task.id)}
                            >
                              <div className="flex w-full flex-col px-3 py-2">
                                <div className="hub-task-title-row">
                                  <Tooltip content={expandedTasks[`${member.id}-${task.id}`] ? 'Click to collapse' : 'Click to expand'}>
                                    <span className="hub-task-expand-icon shrink-0">
                                      {expandedTasks[`${member.id}-${task.id}`] ? (
                                        <FaMinusSquare size={12} />
                                      ) : (
                                        <FaPlusSquare size={12} />
                                      )}
                                    </span>
                                  </Tooltip>
                                  <TaskTypeIcon taskType={task.task_type} className="shrink-0" />
                                  <div className="hub-task-title-wrapper">
                                    <Tooltip 
                                      key={task.id} 
                                      title={task.title}
                                      description={task.description}
                                    >
                                      <div className="hub-task-title-cell">
                                        {(task.display_id || task.project?.name) && (
                                          <div className="hub-task-id-row">
                                            {task.display_id && (
                                              <span className="hub-task-id">
                                                {task.display_id}
                                              </span>
                                            )}
                                            {task.project?.name && (
                                              <ProjectBadge projectName={task.project.name} />
                                            )}
                                            {task.feature_tag && (
                                    <ClickableFeatureTag
                                      featureTag={task.feature_tag}
                                      allFeatureTags={allFeatureTagsFromHub}
                                      allTasks={allTasksFromHub}
                                      size="small"
                                    />
                                  )}
                                          </div>
                                        )}
                                        <span className="hub-task-title-text">
                                          {task.title}
                                        </span>
                                      </div>
                                    </Tooltip>
                                  </div>
                                  <div className="hub-task-explore-wrap">
                                    <Tooltip content="View Task Summary">
                                      <button
                                        className="explore-task-button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openTaskSummaryModal(task, member.id);
                                        }}
                                        aria-label="View task summary"
                                      >
                                        <FeatherArrowRight size={16} />
                                      </button>
                                    </Tooltip>
                                  </div>
                                </div>
                                <div className="hub-task-meta-row">
                                  <TaskNotifications 
                                    taskId={task.id} 
                                    taskTitle={task.title}
                                    notification={getNotification(task.id)}
                                    isLoading={isTaskLoading(task.id)}
                                  />
                                  <ManagerDiscussionMarker count={conversationCounts[task.id]} />
                                  {/* {task.feature_tag && (
                                    <ClickableFeatureTag
                                      featureTag={task.feature_tag}
                                      allFeatureTags={allFeatureTagsFromHub}
                                      allTasks={allTasksFromHub}
                                      size="small"
                                    />
                                  )} */}
                                  <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} />
                                  {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
                                    <Tooltip content={`${task.work_plan_attachments.length} attachment${task.work_plan_attachments.length !== 1 ? 's' : ''}`}>
                                      <div className="hub-attachment-indicator">
                                        <FeatherFileSmall size={8} />
                                      </div>
                                    </Tooltip>
                                  )}
                                  {task.task_pull_requests && task.task_pull_requests.length > 0 && (
                                    <Tooltip content={`${task.task_pull_requests.length} pull request${task.task_pull_requests.length !== 1 ? 's' : ''}`}>
                                      <div className="hub-pull-request-indicator">
                                        <FeatherEdit2 size={6} />
                                      </div>
                                    </Tooltip>
                                  )}
                                  <Badge variant={getTaskStatusVariant(task.status)} className="hub-task-status-badge">
                                    {formatTaskStatusLabel(task.status)}
                                  </Badge>
                                  <Badge variant="neutral" className="hub-task-hours-badge">{formatHours(task.hours)}h</Badge>
                                </div>
                              </div>
                              
                              {expandedTasks[`${member.id}-${task.id}`] && (
                                <div className="px-4 py-4 border-t border-neutral-200">
                                  {/* Task Description */}
                                  <div className="mb-4" style={{ paddingBottom: '5.5px' }}>
                                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Description</h4>
                                    <p className="text-xs text-gray-600 whitespace-pre-wrap" style={{ color: '#616363' }}>{task.description}</p>
                                  </div>

                                  {/* Work Description */}
                                  {task.notes && (
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                      <h4 className="text-xs font-semibold text-gray-700 mb-4">Work Description</h4>
                                      <p className="text-xs whitespace-pre-wrap" style={{ color: '#3367ab' }}>{task.notes}</p>
                                    </div>
                                  )}

                                  {/* Work Plan Attachments */}
                                  {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                      <h4 className="text-xs font-semibold text-gray-700 mb-4">Attachments</h4>
                                      <ul className="hub-attachments-list text-sm">
                                        {task.work_plan_attachments.map(att => (
                                          <li key={att.id} className="hub-attachment-item flex items-center gap-2 mb-2">
                                            <a
                                              href={att.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="hub-attachment-link text-blue-700 hover:underline flex items-center gap-2"
                                            >
                                              <FeatherFileSmall size={26} />
                                              <span>{att.file_name}</span>
                                              <span className="hub-file-size text-xs text-gray-500">({(att.file_size / 1024).toFixed(1)} KB)</span>
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Task Pull Requests */}
                                  {task.task_pull_requests && task.task_pull_requests.length > 0 && (
                                    <div className="mt-6 pt-6 border-t border-gray-200" style={{ backgroundColor: 'rgb(255 255 255)', border:'0.5pt dashed #1b7003', borderRadius: '7pt', padding: '7.5pt', marginTop: '7pt' }}>
                                                                              <div 
                                          className="flex items-center justify-between cursor-pointer p-2 rounded transition-colors"
                                          style={{ 
                                            cursor: 'pointer',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            transition: 'background-color 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f8f8'}
                                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleTaskPullRequestExpansion(`${member.id}-${task.id}`);
                                        }}
                                      >
                                        <div className="flex items-center gap-2">
                                          <h4 className="text-xs font-semibold text-gray-700">Pull Requests</h4>
                                          <span className="text-xs text-gray-500">({task.task_pull_requests.length})</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs text-gray-500">{expandedTaskPullRequests[`${member.id}-${task.id}`] ? '' : ''}</span>
                                          <button className="text-gray-500 hover:text-gray-700 font-bold">
                                            {expandedTaskPullRequests[`${member.id}-${task.id}`] ? '−' : '+'}
                                          </button>
                                        </div>
                                      </div>
                                      {expandedTaskPullRequests[`${member.id}-${task.id}`] && (
                                        <div className="space-y-2 mt-3">
                                          {task.task_pull_requests.map((pr, index) => (
                                            <div key={pr.id} className="flex items-center gap-2">
                                              <div className="hub-pull-request-indicator">
                                                <FeatherEdit2 size={14} />
                                              </div>
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                  <a
                                                    href={pr.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-700 hover:underline text-xs font-medium"
                                                  >
                                                    PR #{index + 1}
                                                  </a>
                                                  {pr.description && (
                                                    <span className="text-xs text-gray-600 break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                                      - {pr.description}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )
                      ) : (
                        <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-3 py-2">
                          <IconWithBackground
                            variant="error"
                            icon={<FeatherAlertTriangle />}
                          />
                          <span className="text-body font-body text-error-900">
                            No tasks planned
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* General Pull Requests Section */}
                  {member.general_pull_requests && member.general_pull_requests.length > 0 && (
                    <div className="flex w-full flex-col items-start gap-4 mt-4" style={{ backgroundColor: 'rgb(255 255 255)', border:'0.5pt dashed #1b7003', borderRadius: '7pt', padding: '7.5pt' }}>
                      <div 
                        className="flex w-full items-center justify-between cursor-pointer p-2 rounded transition-colors"
                        style={{ 
                          cursor: 'pointer',
                          padding: '8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f8f8'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        onClick={() => toggleGeneralPullRequestExpansion(member.id)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">General Pull Requests</span>
                          <span className="text-xs text-gray-500">({member.general_pull_requests.length})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">{expandedGeneralPullRequests[member.id] ? '' : ''}</span>
                          <button className="text-gray-500 hover:text-gray-700 font-bold">
                            {expandedGeneralPullRequests[member.id] ? '−' : '+'}
                          </button>
                        </div>
                      </div>
                      {expandedGeneralPullRequests[member.id] && (
                        <div className="flex w-full flex-col items-start gap-2">
                          {member.general_pull_requests.map((pr, index) => (
                            <div key={pr.id} className="flex w-full flex-col rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
                              <div className="flex w-full items-center gap-2">
                                <IconWithBackground icon={<FeatherFile />} size="small" />
                                <div className="flex items-center gap-2 grow">
                                  <span className="text-body font-body text-default-font">
                                    PR #{index + 1}
                                  </span>
                                  <a
                                    href={pr.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-700 hover:underline text-sm"
                                  >
                                    View Pull Request
                                  </a>
                                </div>
                              </div>
                              {pr.description && (
                                <div className="mt-2 ml-6">
                                  <p className="text-xs text-gray-600 whitespace-pre-wrap break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>{pr.description}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              );
              })}
            </div>
            )}
          </>
        )}

        {/* Show loading state for team grid when main data is loading */}
        {mainDataLoading && (
          <div className="hub-todays-plans-section">
            <div className="hub-todays-plans-header">
              <div className="flex items-center gap-2">
                <IconWithBackground icon={<FeatherClock />} />
                <h2>{isToday(selectedDate) ? "Today's Work Plans" : "Work Plans"}</h2>
              </div>
              <div className="hub-todays-plans-date">
                {formatDateForDisplay(selectedDate)}
              </div>
            </div>
            <div className="flex items-center justify-center py-8">
              <BunnyLoader 
                size="large" 
                text="Loading team overview..."
                animation="bounce"
              />
            </div>
          </div>
        )}

        {/* Task Summary Modal */}
        <TaskSummaryModal
          task={selectedTaskForModal}
          isOpen={!!selectedTaskForModal}
          onClose={closeTaskSummaryModal}
          showManagerDiscussions={true}
          showWorkDescriptions={true}
          getTaskStatus={selectedTaskForModal ? (taskId) => {
            // Find which member this task belongs to
            for (const [memberId, tasks] of Object.entries(memberTasks)) {
              if (tasks.some(t => t.id === taskId)) {
                return getTaskStatus(taskId, memberId);
              }
            }
            return 'open';
          } : undefined}
          getStatusVariant={getTaskStatusVariant}
          getTaskHistory={selectedTaskForModal ? (taskId) => {
            // Find which member this task belongs to
            for (const [memberId, tasks] of Object.entries(memberTasks)) {
              if (tasks.some(t => t.id === taskId)) {
                return getTaskHistory(taskId, memberId);
              }
            }
            return [];
          } : undefined}
        />
      </div>
    </DefaultPageLayout>
  );
};

export default TheHub;